import React, { useEffect, useMemo, useState } from "react";
import { Card, Button, Row, Col, ProgressBar } from "react-bootstrap";
import { FaGithub, FaTwitter, FaInstagram, FaFacebook, FaGlobe, FaStar } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/vi";
moment.locale("vi");

import { useAppDispatch, useAppSelector } from "../../hooks";
import { studentActions, attendanceActions, attendanceStudentRecordActions, userActions, classActions } from "../../redux/actions";
import Loading from "../../common/Loading/loading";
import { pathConstants } from "../../constants";
import { AttendanceAlertModal, AttendanceAlertType } from "../AttendanceManage/AttendanceAlertModal";

interface AttendanceSummary {
  month: number;
  year: number;
  totalPresent: number;
  totalAbsent: number;
  daysPresent: number[];
  daysAbsent: number[];
  classDaysPresentMap: Record<string, number[]>; 
  classDaysAbsentMap: Record<string, number[]>;  
}

const StudentInformation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [alerts, setAlerts] = useState<{ teacherName: string; messages: string[]; attendanceAlertType: AttendanceAlertType }[]>([]);
  const [giftValue, setGiftValue] = useState<number>(0);
  const [isGiftModified, setIsGiftModified] = useState(false);

  const student = useAppSelector((state: any) => state.students.studentsList);
  const classes = useAppSelector((state: any) => state.classes.classesList);
  const attendanceSessions = useAppSelector((state: any) => state.attendanceSessions.attendanceSessionsList);
  const attendanceStudentRecords = useAppSelector((state: any) => state.attendanceStudentRecords.attendanceStudentRecordsList);
  const userInfo = useAppSelector((state: any) => state.authentication.userInfo);

  // 🔹 Gọi API giống style AttendanceManage
  useEffect(() => {
    if (!id) return;
    setIsLoading(true);

    const currentDate = new Date();
    const months: string[] = [];

    // ✅ Lặp để lấy 3 tháng gần nhất (hiện tại + 2 tháng trước)
    for (let i = 0; i < 3; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      months.push(`${year}-${month}`);
    }

    Promise.all([
      dispatch(classActions.getAll()),
      dispatch(studentActions.getById(Number(id))),
      dispatch(attendanceActions.getAllAttendanceSessions()),
      // ✅ Gọi API mới lấy dữ liệu 3 tháng gần nhất của học sinh này
      dispatch(
        attendanceStudentRecordActions.getAttendanceStudentRecordByMonthsAndStudent(
          months,
          Number(id)
        )
      ),
    ])
      .then(() => {
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching student data:", err);
        setIsLoading(false);
      });
  }, [dispatch, id]);

  // Initialize gift value when student data is loaded
  useEffect(() => {
    if (student && student[0]) {
      setGiftValue(student[0].gift || 0);
      setIsGiftModified(false);
    }
  }, [student]);

  // 🔹 Tính thống kê điểm danh
  const attendanceSummary: AttendanceSummary[] = useMemo(() => {
    if (!attendanceSessions?.length || !attendanceStudentRecords?.length || !classes?.length)
      return [];

    const monthsToShow = [0, 1, 2].map((i) => {
      const date = moment().subtract(i, "months");
      return { month: date.month() + 1, year: date.year() };
    });

    return monthsToShow.map(({ month, year }) => {
      const monthSessions = attendanceSessions.filter((s: any) => {
        const d = moment(s.date);
        return d.month() + 1 === month && d.year() === year;
      });
      const monthSessionIds = monthSessions.map((s: any) => s.id);

      const studentRecords = attendanceStudentRecords.filter(
        (r: any) => r.studentId === Number(id) && monthSessionIds.includes(r.sessionId)
      );

      const classDaysPresentMap: Record<string, number[]> = {};
      const classDaysAbsentMap: Record<string, number[]> = {};

      studentRecords.forEach((r: any) => {
        const session = attendanceSessions.find((s: any) => s.id === r.sessionId);
        if (!session) return;
        const className = classes.find((c: any) => c.id === session.classId)?.name || "Không rõ lớp";
        const day = moment(session.date).date();

        if (r.isPresent) {
          if (!classDaysPresentMap[className]) classDaysPresentMap[className] = [];
          classDaysPresentMap[className].push(day);
        } else {
          if (!classDaysAbsentMap[className]) classDaysAbsentMap[className] = [];
          classDaysAbsentMap[className].push(day);
        }
      });

      const daysPresent = Object.values(classDaysPresentMap).flat().sort((a, b) => a - b);
      const daysAbsent = Object.values(classDaysAbsentMap).flat().sort((a, b) => a - b);

      return {
        month,
        year,
        totalPresent: daysPresent.length,
        totalAbsent: daysAbsent.length,
        daysPresent,
        daysAbsent,
        classDaysPresentMap,
        classDaysAbsentMap,
      };
    });
  }, [attendanceSessions, attendanceStudentRecords, id, classes]);

  const handleGiftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value) || 0;
    setGiftValue(newValue);
    setIsGiftModified(newValue !== (student[0]?.gift || 0));
  };

  const handleSaveGift = async () => {
    if (!student[0]?.id) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/students/${student[0].id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ gift: giftValue }),
      });

      if (response.ok) {
        // Reload student data to get updated gift value
        await dispatch(studentActions.getById(Number(id)));
        setIsGiftModified(false);
        alert('✅ Đã lưu số tích quà thành công!');
      } else {
        alert('❌ Không thể lưu số tích quà');
      }
    } catch (error) {
      console.error('Error saving gift:', error);
      alert('❌ Lỗi khi lưu số tích quà');
    }
  };

  const projects = [
    { name: "Web Design", progress: 80 },
    { name: "Website Markup", progress: 72 },
    { name: "One Page", progress: 89 },
    { name: "Mobile Template", progress: 55 },
    { name: "Backend API", progress: 66 },
  ];

  if (isLoading) return <Loading message="Đang tải thông tin học sinh..." />;

  return (
    <div className="container py-4" style={{ backgroundColor: "#f4f7fa", minHeight: "100vh" }}>
      <AttendanceAlertModal show={showModal} alerts={alerts} onClose={() => setShowModal(false)} />

      <Row>
        {/* LEFT PROFILE CARD */}
        <Col md={4}>
          <Card className="text-center shadow-sm border-0">
            <Card.Body>
              <img
                src={
                  student[0]?.avatar 
                    ? `${process.env.REACT_APP_API_URL}${student[0].avatar}`
                    : "https://bootdey.com/img/Content/avatar/avatar7.png"
                }
                alt="Profile"
                className="rounded-circle mb-3"
                style={{ width: "120px", height: "120px", objectFit: "cover" }}
              />
              <h5 className="mb-0">{student[0].name || "Unknown"}</h5>
              
              {/* Gift stars display with editable number input */}
              <div className="d-flex align-items-center justify-content-center mb-3 mt-3" style={{ gap: "12px" }}>
                <input
                  type="number"
                  min="0"
                  value={giftValue}
                  onChange={handleGiftChange}
                  style={{
                    width: "90px",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    textAlign: "center",
                    fontSize: "20px"
                  }}
                />
                <FaStar color="#ffc107" size={32} />
              </div>

              {/* Conditional Save button - only show when gift is modified */}
              {isGiftModified && (
                <div className="d-flex justify-content-center mb-3">
                  <Button variant="success" size="sm" onClick={handleSaveGift}>
                    Lưu
                  </Button>
                </div>
              )}
              
              <hr />
              <div className="text-left px-3">
                <p>
                  <FaGlobe className="mr-2 text-secondary" /> Website:{" "}
                  <span className="text-primary">https://bootdey.com</span>
                </p>
                <p>
                  <FaGithub className="mr-2 text-dark" /> Github:{" "}
                  <span className="text-primary">bootdey</span>
                </p>
                <p>
                  <FaTwitter className="mr-2 text-info" /> Twitter:{" "}
                  <span className="text-primary">@bootdey</span>
                </p>
                <p>
                  <FaInstagram className="mr-2 text-danger" /> Instagram:{" "}
                  <span className="text-primary">bootdey</span>
                </p>
                <p>
                  <FaFacebook className="mr-2 text-primary" /> Facebook:{" "}
                  <span className="text-primary">bootdey</span>
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* RIGHT INFO */}
        <Col md={8}>
          <Card className="mb-3 shadow-sm border-0">
            <Card.Body>
              <h5 className="mb-3">Thống kê điểm danh 3 tháng gần nhất</h5>
              {attendanceSummary.map((s) => (
                <div key={`${s.month}-${s.year}`} className="mb-3">
                  <strong>Tháng {s.month}/{s.year} - {student[0].name}</strong>
                    <div>
                      <strong>Đi học:</strong> {s.totalPresent} buổi{" "}
                      {s.daysPresent.length > 0 && <>— Ngày: {s.daysPresent.join(", ")}</>}
                      {/* Hiển thị chi tiết nếu các đi nghỉ học trong từ 2 lớp trở lên */}
                      {Object.keys(s.classDaysPresentMap || {}).length > 1 && (
                        <ul style={{ marginTop: "4px" }}>
                          {Object.entries(s.classDaysPresentMap || {}).map(([className, days]) => {
                            const dayList = days as number[];
                            return (
                              <li key={className}>
                                <span style={{ fontWeight: 500 }}>{className}:</span>{" "}
                                Ngày {dayList.sort((a, b) => a - b).join(", ")}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                    <div style={{ color: "red" }}>
                      <strong>Nghỉ học:</strong> {s.totalAbsent} buổi{" "}
                      {s.daysAbsent.length > 0 && <>— Ngày: {s.daysAbsent.join(", ")}</>}
                      {/* Hiển thị chi tiết nếu các buổi nghỉ học trong từ 2 lớp trở lên */}
                      {Object.keys(s.classDaysAbsentMap || {}).length > 1 && (
                        <ul style={{ marginTop: "4px" }}>
                          {Object.entries(s.classDaysAbsentMap || {}).map(([className, days]) => (
                            <li key={className}>
                              <span style={{ fontWeight: 500 }}>{className}:</span>{" "}
                              Ngày {days.sort((a, b) => a - b).join(", ")}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  <hr />
                </div>
              ))}
            </Card.Body>
          </Card>
          
          {/* PROJECTS */}
          <Row>
            <Col md={6}>
              <Card className="shadow-sm border-0 mb-3">
                <Card.Body>
                  <h6 className="text-muted text-uppercase mb-2">Assignment</h6>
                  <h5 className="mb-3">Project Status</h5>
                  {projects.map((p, i) => (
                    <div key={i} className="mb-2">
                      <small>{p.name}</small>
                      <ProgressBar now={p.progress} label={`${p.progress}%`} className="mb-1" />
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="shadow-sm border-0 mb-3">
                <Card.Body>
                  <h6 className="text-muted text-uppercase mb-2">Assignment</h6>
                  <h5 className="mb-3">Project Status</h5>
                  {projects.map((p, i) => (
                    <div key={i} className="mb-2">
                      <small>{p.name}</small>
                      <ProgressBar now={p.progress} label={`${p.progress}%`} className="mb-1" />
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ATTENDANCE SUMMARY */}
          <Card className="shadow-sm border-0">
            <Card.Body>
              <Row>
                <Col sm={3}>
                  <h6 className="mb-0">Full Name</h6>
                </Col>
                <Col sm={9} className="text-secondary">
                  {student[0].name || 'N/A'}
                </Col>
              </Row>
              <hr />
              <Row>
                <Col sm={3}>
                  <h6 className="mb-0">Email</h6>
                </Col>
                <Col sm={9} className="text-secondary">
                  {student[0].email || "N/A"}
                </Col>
              </Row>
              <hr />
              <Row>
                <Col sm={3}>
                  <h6 className="mb-0">Phone</h6>
                </Col>
                <Col sm={9} className="text-secondary">
                  {student[0].phoneNumber || "N/A"}
                </Col>
              </Row>
              <hr />
              <Row>
                <Col sm={3}>
                  <h6 className="mb-0">Mobile</h6>
                </Col>
                <Col sm={9} className="text-secondary">
                  {student?.mobile || "N/A"}
                </Col>
              </Row>
              <hr />
              <Row>
                <Col sm={3}>
                  <h6 className="mb-0">Address</h6>
                </Col>
                <Col sm={9} className="text-secondary">
                  {student?.address || "N/A"}
                </Col>
              </Row>
              <div className="text-right mt-3">
                <Button variant="primary">Edit</Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default StudentInformation;




// import React from "react";
// import { Card, Button, Row, Col, ProgressBar, Table } from "react-bootstrap";
// import { FaGithub, FaTwitter, FaInstagram, FaFacebook, FaGlobe } from "react-icons/fa";
// import { useParams } from "react-router-dom";

// const StudentInformation: React.FC = () => {
//   const { id } = useParams<{ id: string }>();
//   const projects = [
//     { name: "Web Design", progress: 80 },
//     { name: "Website Markup", progress: 72 },
//     { name: "One Page", progress: 89 },
//     { name: "Mobile Template", progress: 55 },
//     { name: "Backend API", progress: 66 },
//   ];

//   return (
//     <div className="container py-4" style={{ backgroundColor: "#f4f7fa", minHeight: "100vh" }}>
//       <Row>
//         {/* LEFT PROFILE CARD */}
//         <Col md={4}>
//           <Card className="text-center shadow-sm border-0">
//             <Card.Body>
//               <img
//                 src="https://bootdey.com/img/Content/avatar/avatar7.png"
//                 alt="Profile"
//                 className="rounded-circle mb-3"
//                 style={{ width: "120px", height: "120px" }}
//               />
//               <h5 className="mb-0">John Doe</h5>
//               <small className="text-muted">Full Stack Developer</small>
//               <p className="text-muted mb-3">Bay Area, San Francisco, CA</p>
//               <div className="d-flex justify-content-center mb-3">
//                 <Button variant="primary" size="sm" className="mr-2">
//                   Follow
//                 </Button>
//                 <Button variant="outline-primary" size="sm">
//                   Message
//                 </Button>
//               </div>
//               <hr />
//               <div className="text-left px-3">
//                 <p><FaGlobe className="mr-2 text-secondary" /> Website: <span className="text-primary">https://bootdey.com</span></p>
//                 <p><FaGithub className="mr-2 text-dark" /> Github: <span className="text-primary">bootdey</span></p>
//                 <p><FaTwitter className="mr-2 text-info" /> Twitter: <span className="text-primary">@bootdey</span></p>
//                 <p><FaInstagram className="mr-2 text-danger" /> Instagram: <span className="text-primary">bootdey</span></p>
//                 <p><FaFacebook className="mr-2 text-primary" /> Facebook: <span className="text-primary">bootdey</span></p>
//               </div>
//             </Card.Body>
//           </Card>
//         </Col>

//         {/* RIGHT INFO + PROGRESS */}
//         <Col md={8}>
//           <Card className="mb-3 shadow-sm border-0">
//             <Card.Body>
//               <Row>
//                 <Col sm={3}>
//                   <h6 className="mb-0">Full Name</h6>
//                 </Col>
//                 <Col sm={9} className="text-secondary">
//                   Kenneth Valdez
//                 </Col>
//               </Row>
//               <hr />
//               <Row>
//                 <Col sm={3}>
//                   <h6 className="mb-0">Email</h6>
//                 </Col>
//                 <Col sm={9} className="text-secondary">
//                   fip@jukmuh.al
//                 </Col>
//               </Row>
//               <hr />
//               <Row>
//                 <Col sm={3}>
//                   <h6 className="mb-0">Phone</h6>
//                 </Col>
//                 <Col sm={9} className="text-secondary">
//                   (239) 816-9029
//                 </Col>
//               </Row>
//               <hr />
//               <Row>
//                 <Col sm={3}>
//                   <h6 className="mb-0">Mobile</h6>
//                 </Col>
//                 <Col sm={9} className="text-secondary">
//                   (320) 380-4539
//                 </Col>
//               </Row>
//               <hr />
//               <Row>
//                 <Col sm={3}>
//                   <h6 className="mb-0">Address</h6>
//                 </Col>
//                 <Col sm={9} className="text-secondary">
//                   Bay Area, San Francisco, CA
//                 </Col>
//               </Row>
//               <div className="text-right mt-3">
//                 <Button variant="primary">Edit</Button>
//               </div>
//             </Card.Body>
//           </Card>

//           <Row>
//             {/* PROJECT STATUS 1 */}
//             <Col md={6}>
//               <Card className="shadow-sm border-0 mb-3">
//                 <Card.Body>
//                   <h6 className="text-muted text-uppercase mb-2">Assignment</h6>
//                   <h5 className="mb-3">Project Status</h5>
//                   {projects.map((p, i) => (
//                     <div key={i} className="mb-2">
//                       <small>{p.name}</small>
//                       <ProgressBar now={p.progress} label={`${p.progress}%`} className="mb-1" />
//                     </div>
//                   ))}
//                 </Card.Body>
//               </Card>
//             </Col>

//             {/* PROJECT STATUS 2 */}
//             <Col md={6}>
//               <Card className="shadow-sm border-0 mb-3">
//                 <Card.Body>
//                   <h6 className="text-muted text-uppercase mb-2">Assignment</h6>
//                   <h5 className="mb-3">Project Status</h5>
//                   {projects.map((p, i) => (
//                     <div key={i} className="mb-2">
//                       <small>{p.name}</small>
//                       <ProgressBar now={p.progress} label={`${p.progress}%`} className="mb-1" />
//                     </div>
//                   ))}
//                 </Card.Body>
//               </Card>
//             </Col>
//           </Row>
//         </Col>
//       </Row>
//     </div>
//   );
// };

// export default StudentInformation;
